import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, CreditCard, History, LogOut, Search, Shield, Globe, Mail, Trash2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import Logo from '../components/Logo';
import api from '../services/api';

const Dashboard = () => {
  const { user, logout, refreshUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationMessage, setVerificationMessage] = useState('');

  useEffect(() => {
    // Check for successful payment
    const payment = searchParams.get('payment');
    if (payment === 'success') {
      refreshUser();
    }
  }, [searchParams, refreshUser]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleResendVerification = async () => {
    try {
      await api.post('/auth/resend-verification', { email: user.email });
      setVerificationMessage('Verification email sent! Please check your inbox.');
      setTimeout(() => setVerificationMessage(''), 5000);
    } catch (error) {
      setVerificationMessage('Failed to send verification email. Please try again.');
      setTimeout(() => setVerificationMessage(''), 5000);
    }
  };

  const features = [
    {
      icon: Search,
      title: 'Comprehensive Research',
      description: 'AI-powered analysis using multiple data sources including corporate registries, court records, and sanctions lists.'
    },
    {
      icon: Shield,
      title: 'Detailed Risk Assessment',
      description: 'Structured risk scoring methodology with individual category breakdowns and clear recommendations.'
    },
    {
      icon: Globe,
      title: 'Global Coverage',
      description: 'Due diligence on entities worldwide with jurisdiction-specific regulatory analysis.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            <nav className="flex items-center gap-6">
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Plans
              </Link>
              <Link to="/reports" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Reports
              </Link>
              {isAdmin && (
                <Link to="/admin-portal-7x9k2m/dashboard" className="flex items-center gap-1 text-red-700 hover:text-red-900 text-sm font-medium">
                  <Shield className="h-4 w-4" />
                  Admin
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium flex items-center gap-1"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome back, {user?.name?.split(' ')[0] || 'User'}
              </h1>
              <p className="text-gray-600 mt-1">
                {user?.emailVerified 
                  ? 'Ready to generate your next due diligence report.'
                  : 'Please verify your email to generate reports.'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Reports Remaining</p>
                <p className="text-2xl font-bold text-gray-900">
                  {user?.subscription?.reportsRemaining || 0}
                </p>
              </div>
              <Link
                to="/reports/new"
                className={`inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  user?.subscription?.reportsRemaining > 0 && user?.emailVerified
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                <FileText className="h-5 w-5 mr-2" />
                New Report
              </Link>
            </div>
          </div>
        </div>

        {/* Email verification notification */}
        {!user?.emailVerified && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Email Verification Required</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Please verify your email address to start generating reports.
                  </p>
                  {verificationMessage && (
                    <p className="text-sm text-green-700 mt-2 font-medium">{verificationMessage}</p>
                  )}
                </div>
              </div>
              <button
                onClick={handleResendVerification}
                className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition-colors"
              >
                Verify Email Now
              </button>
            </div>
          </div>
        )}

        {/* Payment success notification */}
        {searchParams.get('payment') === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
            <p className="text-green-800">
              Payment successful! Your report credits have been added to your account.
            </p>
          </div>
        )}

        {/* Subscription info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Current Plan</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900 capitalize">
              {user?.subscription?.plan === 'none' ? 'No Plan' : user?.subscription?.plan?.replace('_', ' ') || 'No Plan'}
            </p>
            {user?.subscription?.renewalDate && (
              <p className="text-sm text-gray-500 mt-1">
                Renews: {new Date(user.subscription.renewalDate).toLocaleDateString('en-GB')}
              </p>
            )}
            <Link to="/pricing" className="text-red-600 hover:text-red-700 text-sm font-medium mt-3 inline-block">
              {user?.subscription?.plan === 'none' ? 'Get Started →' : 'Upgrade →'}
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Available Reports</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {user?.subscription?.reportsRemaining || 0}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {user?.subscription?.reportsPerMonth > 0 
                ? `${user.subscription.reportsPerMonth} per month included`
                : 'Pay-as-you-go available'}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <History className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Report History</h3>
            </div>
            <Link
              to="/reports"
              className="text-red-600 hover:text-red-700 text-sm font-medium"
            >
              View all reports →
            </Link>
          </div>
        </div>

        {/* Features */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">What You Get</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white rounded-lg shadow-sm p-6">
                <div className="p-2 bg-gray-100 rounded-lg w-fit mb-4">
                  <feature.icon className="h-6 w-6 text-gray-700" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        {user?.subscription?.reportsRemaining === 0 && (
          <div className="bg-red-50 rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Ready to Get Started?
            </h3>
            <p className="text-gray-600 mb-4">
              Purchase a report or subscribe to a plan to begin generating due diligence reports.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
            >
              View Plans
            </Link>
          </div>
        )}
      </main>

      {/* Account Settings */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="border border-red-200 rounded-lg p-6 bg-red-50">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Danger Zone</h3>
          <p className="text-sm text-red-700 mb-4">
            Permanently delete your account and all associated data. This action has a 30-day grace period during which you can contact support to reverse it.
          </p>
          <button
            onClick={async () => {
              const confirmed = window.confirm(
                'Are you sure you want to delete your account? All your reports and data will be scheduled for permanent deletion after a 30-day grace period.'
              );
              if (!confirmed) return;
              const emailConfirm = window.prompt('To confirm, please type your email address:');
              if (emailConfirm !== user?.email) {
                alert('Email does not match. Account deletion cancelled.');
                return;
              }
              try {
                await api.post('/user/delete-account', { email: emailConfirm });
                alert('Account deletion requested. You have 30 days to contact support if you change your mind.');
                logout();
                navigate('/');
              } catch (err) {
                alert(err.message || 'Failed to delete account.');
              }
            }}
            className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete My Account
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Totalinfo Ltd. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm flex-wrap">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-700">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-700">Terms of Service</Link>
              <Link to="/cookies" className="text-gray-500 hover:text-gray-700">Cookie Policy</Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('id100:open-cookie-prefs'))}
                className="text-gray-500 hover:text-gray-700 bg-transparent border-0 p-0 cursor-pointer"
              >Cookie preferences</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
